import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button.jsx';
import { Label } from '../ui/label.jsx';
import { Textarea } from '../ui/textarea.jsx';
import { Checkbox } from '../ui/checkbox.jsx';
import { CheckCircle2, Lock } from 'lucide-react';

const toAnswerMap = (list) => Object.fromEntries((list || []).map(a => [a.question_id, a.answer]));

const SingleOptions = ({ q, idx, value, onChange, disabled }) => (
  <div className="space-y-1.5">
    {q.options.map((o, i) => (
      <label key={i} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded">
        <input type="radio" name={q.id} checked={value === o} onChange={() => onChange(o)} disabled={disabled} data-testid={`opt-${idx}-${i}`} />
        <span className="text-sm">{o}</span>
      </label>
    ))}
  </div>
);

const MultipleOptions = ({ q, idx, value, onToggle, disabled }) => {
  const cur = Array.isArray(value) ? value : [];
  return (
    <div className="space-y-1.5">
      {q.options.map((o, i) => (
        <label key={i} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-2 rounded">
          <Checkbox checked={cur.includes(o)} onCheckedChange={() => onToggle(o)} disabled={disabled} data-testid={`opt-${idx}-${i}`} />
          <span className="text-sm">{o}</span>
        </label>
      ))}
    </div>
  );
};

const QuestionBlock = ({ q, idx, value, setValue, toggle, disabled }) => (
  <div className="border-b pb-4 last:border-b-0 last:pb-0" data-testid={`q-${idx}`}>
    <Label className="text-sm font-semibold text-slate-800">
      {idx + 1}. {q.text}
      {q.type === 'multiple' && q.max_selections && (
        <span className="ml-2 text-xs text-slate-400 font-normal">(máx. {q.max_selections} seleções)</span>
      )}
    </Label>
    <div className="mt-2">
      {q.type === 'single' && <SingleOptions q={q} idx={idx} value={value} onChange={setValue} disabled={disabled} />}
      {q.type === 'multiple' && <MultipleOptions q={q} idx={idx} value={value} onToggle={toggle} disabled={disabled} />}
      {q.type === 'open' && (
        <Textarea rows={3} value={value || ''} onChange={e => setValue(e.target.value)} placeholder="Sua resposta..." disabled={disabled} data-testid={`open-${idx}`} />
      )}
    </div>
  </div>
);

export const DiagnosticForm = ({ questions, initialAnswers, locked, saving, hasPrevious, onSubmit }) => {
  const [answers, setAnswers] = useState(() => toAnswerMap(initialAnswers));

  const setValue = (qid, val) => setAnswers(p => ({ ...p, [qid]: val }));
  const toggle = (q, val) => setAnswers(p => {
    const cur = Array.isArray(p[q.id]) ? p[q.id] : [];
    if (cur.includes(val)) return { ...p, [q.id]: cur.filter(v => v !== val) };
    if (q.max_selections && cur.length >= q.max_selections) {
      toast.warning(`Máximo de ${q.max_selections} seleções nesta pergunta`);
      return p;
    }
    return { ...p, [q.id]: [...cur, val] };
  });

  const submit = () => {
    const payload = (questions || []).map(q => ({
      question_id: q.id,
      answer: q.type === 'multiple' ? (answers[q.id] || []) : (answers[q.id] || ''),
    }));
    const any = payload.some(a => (Array.isArray(a.answer) ? a.answer.length > 0 : String(a.answer).trim()));
    if (!any) { toast.error('Responda ao menos uma pergunta.'); return; }
    onSubmit(payload);
  };

  return (
    <div data-testid="diagnostic-form">
      {locked && (
        <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg p-3" data-testid="locked-banner">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" />
          Os resultados já foram liberados. Suas respostas estão bloqueadas para edição.
        </div>
      )}
      <div className="space-y-5">
        {(questions || []).map((q, idx) => (
          <QuestionBlock key={q.id} q={q} idx={idx} value={answers[q.id]} setValue={v => setValue(q.id, v)} toggle={v => toggle(q, v)} disabled={locked} />
        ))}
      </div>
      {!locked && (
        <div className="flex justify-end mt-6 pt-4 border-t">
          <Button onClick={submit} disabled={saving} data-testid="submit-diag">
            {saving ? 'Enviando...' : <><CheckCircle2 className="w-4 h-4 mr-1" /> {hasPrevious ? 'Salvar Alterações' : 'Enviar Respostas'}</>}
          </Button>
        </div>
      )}
    </div>
  );
};

export default DiagnosticForm;
